<script setup>
import {computed, ref, watch} from 'vue';

const props = defineProps({
  tree: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(['continue']);

const currentNodeId = ref(null);
const history = ref([]);
const answer = ref(null);

const nodesById = computed(() => new Map((props.tree?.nodes || []).map(node => [node.id, node])));
const currentNode = computed(() => nodesById.value.get(currentNodeId.value) || null);
const canGoBack = computed(() => Boolean(answer.value) || history.value.length > 0);

function reset() {
  currentNodeId.value = props.tree?.root_node_id || null;
  history.value = [];
  answer.value = null;
}

function choose(option) {
  if (option.next_node_id) {
    history.value.push(currentNodeId.value);
    currentNodeId.value = option.next_node_id;
    answer.value = null;
    return;
  }

  answer.value = option.answer || null;
}

function goBack() {
  if (answer.value) {
    answer.value = null;
    return;
  }

  const previous = history.value.pop();
  if (previous) currentNodeId.value = previous;
}

watch(() => props.tree, reset, {deep: true, immediate: true});
</script>

<template>
  <section data-testid="customer-chat-pre-question-flow" class="space-y-3 border-t pt-3">
    <button
      v-if="canGoBack"
      type="button"
      data-testid="customer-chat-pre-question-back"
      class="text-sm font-medium text-stone-600"
      :title="$t('이전 사전 질문으로 돌아가기')"
      @click="goBack"
    >‹ {{ $t('이전') }}</button>

    <div v-if="answer" data-testid="customer-chat-pre-question-answer" class="space-y-3">
      <div class="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-stone-800">
        <strong class="block text-sm">{{ answer.title || $t('안내 답변') }}</strong>
        <p class="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{{ answer.content }}</p>
      </div>
      <p class="text-xs text-stone-500">{{ $t('추가 도움이 필요하면 상담원에게 문의해 주세요.') }}</p>
      <div class="grid grid-cols-2 gap-2">
        <button
          type="button"
          data-testid="customer-chat-pre-question-reset"
          class="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700"
          :title="$t('사전 질문 처음부터 다시 선택')"
          @click="reset"
        >{{ $t('처음부터') }}</button>
        <button
          type="button"
          data-testid="customer-chat-pre-question-continue"
          class="cw-chat-primary"
          :title="$t('상담원에게 문의하기')"
          @click="emit('continue')"
        >{{ $t('상담원에게 문의하기') }}</button>
      </div>
    </div>

    <div v-else-if="currentNode" class="space-y-3">
      <div>
        <h3 class="font-semibold text-stone-800">{{ currentNode.prompt }}</h3>
        <p class="mt-1 text-xs text-stone-500">{{ $t('가장 가까운 질문 유형을 선택해 주세요.') }}</p>
      </div>
      <div class="space-y-2">
        <button
          v-for="option in currentNode.options"
          :key="option.id"
          type="button"
          data-testid="customer-chat-pre-question-option"
          class="w-full rounded-xl border border-stone-200 bg-white p-3 text-left text-sm font-medium text-stone-700 hover:bg-stone-50"
          :title="option.label"
          @click="choose(option)"
        >
          <span class="flex items-center justify-between gap-2">
            <span>{{ option.label }}</span>
            <span aria-hidden="true" class="text-stone-400">›</span>
          </span>
        </button>
      </div>
    </div>

    <button
      v-else
      type="button"
      class="cw-chat-primary w-full"
      :title="$t('상담 시작 화면으로 이동')"
      @click="emit('continue')"
    >{{ $t('상담 시작') }}</button>
  </section>
</template>
